import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRight, Sparkles, Truck, MapPin } from 'lucide-react';
import Navbar from '@/components/site/Navbar';
import Footer from '@/components/site/Footer';
import FloatingActions from '@/components/site/FloatingActions';
import PageFAQ, { buildFaqJsonLd } from '@/components/site/PageFAQ';
import useSEO from '@/hooks/useSEO';

/**
 * Generic keyword-targeted SEO landing page.
 * Used by /indian-clothing-usa, /indian-clothing-morrisville-nc, and
 * /indian-clothing-raleigh-nc — all driven purely by props.
 */
export default function LandingPage({
  slug,
  title,
  description,
  h1,
  intro,
  keyword,
  city,
  faqs = [],
  categories = [],
  jsonLdType = 'WebPage',
}) {
  const canonical = typeof window !== 'undefined'
    ? `${window.location.origin}${window.location.pathname}`
    : `https://trendyclosetusa.com/${slug}`;

  const jsonLd = React.useMemo(() => {
    const arr = [
      {
        '@context': 'https://schema.org',
        '@type': jsonLdType,
        name: h1,
        description,
        url: canonical,
        about: keyword,
        isPartOf: {
          '@type': 'WebSite',
          name: 'Trendy Closet',
          url: 'https://trendyclosetusa.com',
        },
      },
    ];
    if (faqs.length) arr.push(buildFaqJsonLd(faqs));
    return arr;
  }, [h1, description, canonical, keyword, jsonLdType, faqs]);

  useSEO({ title, description, canonical, jsonLd });

  return (
    <main data-testid={`landing-${slug}`} className="min-h-screen bg-ivory text-charcoal">
      <Navbar />

      {/* Hero */}
      <section className="pt-28 lg:pt-36 pb-14 lg:pb-20">
        <div className="container-luxe">
          <nav className="flex flex-wrap items-center gap-2 text-xs uppercase tracking-[0.2em] text-charcoal/55 mb-10">
            <Link to="/" className="hover:text-maroon">Home</Link>
            <span>/</span>
            <span className="text-charcoal">{keyword}</span>
          </nav>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl"
          >
            <span className="overline">Indian Ethnic Wear</span>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl text-charcoal mt-3 leading-[1.05] tracking-tight">
              {h1}
            </h1>
            <div className="mt-6 h-px w-16 bg-gold" />
            <p className="mt-8 text-lg lg:text-xl text-charcoal/80 leading-relaxed">
              {intro}
            </p>
          </motion.div>

          {/* USP row */}
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-4 max-w-5xl">
            <div className="bg-surface border border-brandborder/70 p-6 flex gap-4">
              <Truck className="w-6 h-6 text-maroon shrink-0" aria-hidden="true" />
              <div>
                <div className="font-display text-lg">USA-wide shipping</div>
                <div className="text-sm text-charcoal/65 mt-1">
                  We ship across the United States — from NC to CA, FL to WA.
                </div>
              </div>
            </div>
            <div className="bg-surface border border-brandborder/70 p-6 flex gap-4">
              <MapPin className="w-6 h-6 text-maroon shrink-0" aria-hidden="true" />
              <div>
                <div className="font-display text-lg">Morrisville, NC boutique</div>
                <div className="text-sm text-charcoal/65 mt-1">
                  Visit us at 158 Liberty Rose Dr — Cary, Raleigh, Durham & Apex are minutes away.
                </div>
              </div>
            </div>
            <div className="bg-surface border border-brandborder/70 p-6 flex gap-4">
              <Sparkles className="w-6 h-6 text-maroon shrink-0" aria-hidden="true" />
              <div>
                <div className="font-display text-lg">Owner-styled selection</div>
                <div className="text-sm text-charcoal/65 mt-1">
                  Every piece is hand-picked. Custom sizing & tailoring on request.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Category grid — internal linking for SEO */}
      {categories.length > 0 && (
        <section className="border-t border-brandborder/60 py-16 lg:py-24 bg-surface">
          <div className="container-luxe">
            <span className="overline">Shop by category</span>
            <h2 className="font-display text-3xl lg:text-4xl text-charcoal mt-3 tracking-tight">
              What we carry {city ? `for ${city} customers` : 'nationwide'}
            </h2>
            <div className="mt-4 h-px w-14 bg-gold" />

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {categories.map((c) => (
                <Link
                  key={c.path}
                  to={c.path}
                  data-testid={`landing-cat-${c.path.slice(1)}`}
                  className="group bg-ivory border border-brandborder p-6 lg:p-8 hover:border-maroon transition-colors flex items-start justify-between gap-4"
                >
                  <div>
                    <div className="font-display text-xl text-charcoal group-hover:text-maroon transition-colors">
                      {c.title}
                    </div>
                    <div className="text-sm text-charcoal/65 mt-2 leading-relaxed">
                      {c.body}
                    </div>
                  </div>
                  <ArrowUpRight className="w-5 h-5 text-maroon shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      {faqs.length > 0 && (
        <PageFAQ items={faqs} title="Frequently asked" testId={`landing-faq-${slug}`} />
      )}

      {/* CTA */}
      <section className="py-16 lg:py-24 border-t border-brandborder/60">
        <div className="container-luxe max-w-3xl text-center">
          <span className="overline">Ready to shop?</span>
          <h2 className="font-display text-3xl lg:text-4xl text-charcoal mt-4">
            Chat with the closet on WhatsApp
          </h2>
          <p className="mt-4 text-charcoal/70">
            Send us pictures of your event, share sizes and we&apos;ll curate the pieces we think you&apos;ll love.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href="https://wa.me/19843779835?text=Hi%20Trendy%20Closet%2C%20I%27d%20love%20to%20see%20your%20Indian%20ethnic%20wear%20collection."
              target="_blank" rel="noreferrer"
              data-testid={`landing-cta-whatsapp-${slug}`}
              className="inline-flex items-center gap-2 px-7 py-4 bg-burgundy text-ivory text-xs uppercase tracking-[0.22em] hover:bg-maroon transition-colors"
            >
              WhatsApp us
            </a>
            <Link
              to="/contact"
              data-testid={`landing-cta-contact-${slug}`}
              className="inline-flex items-center gap-2 px-7 py-4 border border-gold text-charcoal hover:bg-gold hover:text-ivory text-xs uppercase tracking-[0.22em] transition-colors"
            >
              Visit the boutique
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingActions />
    </main>
  );
}
